import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Plus,
  Trash2,
  Users,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { EntityDrawer, PageHeader } from "../components/ui";
import { api, ApiError } from "../lib/api";
import type {
  AllocationCheckResponse,
  Beneficiary,
} from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

const RELATIONSHIPS = ["spouse", "child", "parent", "sibling", "other"] as const;
const PAYOUT_KINDS = [
  "life_insurance",
  "gosi_pension",
  "eos_settlement_if_deceased",
] as const;

function NewBeneficiaryDialog({
  legalEntityId,
  defaultEmployeeId,
  onClose,
  onCreated,
}: {
  legalEntityId: string;
  defaultEmployeeId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    employee_id: defaultEmployeeId,
    full_name: "",
    full_name_ar: "",
    relationship: RELATIONSHIPS[0] as string,
    national_id: "",
    iqama: "",
    bank_iban: "",
    primary: true,
    allocation_pct: "100",
    beneficiary_for: ["life_insurance"] as string[],
  });
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () =>
      api.createBeneficiary({
        legal_entity_id: legalEntityId,
        employee_id: form.employee_id,
        full_name: form.full_name,
        full_name_ar: form.full_name_ar,
        relationship: form.relationship,
        national_id: form.national_id || null,
        iqama: form.iqama || null,
        bank_iban: form.bank_iban || null,
        primary: form.primary,
        allocation_pct: form.allocation_pct,
        beneficiary_for: form.beneficiary_for,
      }),
    onSuccess: () => {
      onCreated();
      onClose();
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.detail : String(e));
    },
  });

  function togglePayout(kind: string) {
    setForm((f) => ({
      ...f,
      beneficiary_for: f.beneficiary_for.includes(kind)
        ? f.beneficiary_for.filter((k) => k !== kind)
        : [...f.beneficiary_for, kind],
    }));
  }

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("benefits.beneficiary_new", "Add a beneficiary")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
            disabled={!form.employee_id || !form.full_name}
            className="rounded-lg bg-brand-deep px-4 py-2 text-sm font-medium text-white hover:opacity-95 disabled:opacity-50"
          >
            {t("common.create", "Create")}
          </button>
        </div>
      }
    >
      <div className="space-y-3 p-4 text-sm">
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("benefits.beneficiary_employee_id", "Employee ID (UUID)")}
          </label>
          <input
            value={form.employee_id}
            onChange={(e) =>
              setForm({ ...form, employee_id: e.target.value })
            }
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("benefits.beneficiary_name", "Full name (EN)")}
            </label>
            <input
              value={form.full_name}
              onChange={(e) =>
                setForm({ ...form, full_name: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("benefits.beneficiary_name_ar", "Full name (AR)")}
            </label>
            <input
              dir="rtl"
              value={form.full_name_ar}
              onChange={(e) =>
                setForm({ ...form, full_name_ar: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("benefits.beneficiary_relationship", "Relationship")}
            </label>
            <select
              value={form.relationship}
              onChange={(e) =>
                setForm({ ...form, relationship: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
            >
              {RELATIONSHIPS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("benefits.beneficiary_alloc", "Allocation %")}
            </label>
            <input
              type="number"
              value={form.allocation_pct}
              onChange={(e) =>
                setForm({ ...form, allocation_pct: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("benefits.beneficiary_nid", "National ID")}
            </label>
            <input
              value={form.national_id}
              onChange={(e) =>
                setForm({ ...form, national_id: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("benefits.beneficiary_iqama", "Iqama")}
            </label>
            <input
              value={form.iqama}
              onChange={(e) => setForm({ ...form, iqama: e.target.value })}
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("benefits.beneficiary_iban", "Bank IBAN (SA)")}
          </label>
          <input
            value={form.bank_iban}
            onChange={(e) =>
              setForm({ ...form, bank_iban: e.target.value })
            }
            placeholder="SA00 ..."
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono"
          />
        </div>
        <div>
          <div className="text-xs font-medium text-text-secondary">
            {t("benefits.beneficiary_for", "Beneficiary for")}
          </div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {PAYOUT_KINDS.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => togglePayout(k)}
                className={`rounded-lg border px-2 py-1 text-[11px] ${
                  form.beneficiary_for.includes(k)
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {k.replace(/_/g, " ")}
              </button>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-text-secondary">
          <input
            type="checkbox"
            checked={form.primary}
            onChange={(e) =>
              setForm({ ...form, primary: e.target.checked })
            }
          />
          {t("benefits.beneficiary_primary", "Primary beneficiary")}
        </label>
        {error && (
          <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800">
            {error}
          </div>
        )}
      </div>
    </EntityDrawer>
  );
}

function AllocationCard({
  employeeId,
}: {
  employeeId: string;
}) {
  const { t } = useTranslation();
  const q = useQuery<AllocationCheckResponse>({
    queryKey: ["beneficiary-allocation", employeeId],
    queryFn: () => api.checkBeneficiaryAllocation(employeeId),
    enabled: !!employeeId,
  });
  if (!employeeId) return null;
  if (q.isLoading || !q.data) return <Loading />;
  const tone = q.data.balanced ? "emerald" : "rose";
  return (
    <div
      className={`rounded-2xl border p-3 ${
        tone === "emerald"
          ? "border-emerald-300 bg-emerald-50/50"
          : "border-rose-300 bg-rose-50/40"
      }`}
    >
      <div className="flex items-center gap-2">
        {tone === "emerald" ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-700" />
        ) : (
          <AlertTriangle className="h-4 w-4 text-rose-700" />
        )}
        <h3 className="text-sm font-medium text-text-primary">
          {t("benefits.beneficiary_alloc_check", "Allocation balance")}
        </h3>
      </div>
      <ul className="mt-1 space-y-0.5 text-xs">
        {Object.entries(q.data.sums_by_kind).length === 0 && (
          <li className="text-text-tertiary">
            {t(
              "benefits.beneficiary_no_alloc",
              "No active beneficiary allocations.",
            )}
          </li>
        )}
        {Object.entries(q.data.sums_by_kind).map(([kind, sum]) => (
          <li key={kind}>
            <span className="font-mono text-text-primary">{sum}</span>{" "}
            <span className="text-text-tertiary">{kind.replace(/_/g, " ")}</span>
          </li>
        ))}
      </ul>
      {q.data.issues.length > 0 && (
        <div className="mt-2 text-xs text-rose-800">
          {q.data.issues.map((iss, i) => (
            <div key={i}>{iss}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function BeneficiaryRow({
  b,
  onDelete,
}: {
  b: Beneficiary;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-border-subtle bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-medium text-text-primary">
            {b.full_name}{" "}
            {b.primary && (
              <span className="ml-1 rounded-full bg-brand-blue/15 px-1.5 py-0.5 text-[9px] uppercase text-brand-deep">
                primary
              </span>
            )}
          </div>
          <div className="text-xs text-text-tertiary" dir="rtl">
            {b.full_name_ar}
          </div>
        </div>
        <button
          type="button"
          onClick={onDelete}
          className="rounded-lg border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:bg-surface-sunken"
          title={t("benefits.beneficiary_deactivate", "Deactivate")}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-text-secondary">
        <div>
          <dt className="text-text-tertiary">
            {t("benefits.beneficiary_relationship", "Relationship")}
          </dt>
          <dd>{b.relationship}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">
            {t("benefits.beneficiary_alloc", "Allocation %")}
          </dt>
          <dd className="font-mono">{b.allocation_pct}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">
            {t("benefits.beneficiary_id_doc", "ID")}
          </dt>
          <dd className="font-mono">
            {b.national_id || b.iqama || "—"}
          </dd>
        </div>
        <div>
          <dt className="text-text-tertiary">
            {t("benefits.beneficiary_iban_short", "IBAN")}
          </dt>
          <dd className="font-mono">
            {b.bank_iban
              ? `${b.bank_iban.slice(0, 8)}…${b.bank_iban.slice(-4)}`
              : "—"}{" "}
            {b.bank_iban_validated && (
              <CheckCircle2 className="inline h-3 w-3 text-emerald-600" />
            )}
          </dd>
        </div>
        <div className="col-span-2">
          <dt className="text-text-tertiary">
            {t("benefits.beneficiary_for", "Beneficiary for")}
          </dt>
          <dd>
            {b.beneficiary_for.length === 0
              ? "—"
              : b.beneficiary_for
                  .map((k) => k.replace(/_/g, " "))
                  .join(" · ")}
          </dd>
        </div>
      </dl>
    </div>
  );
}

export function BeneficiariesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [employeeId, setEmployeeId] = useState<string>("");
  const [createOpen, setCreateOpen] = useState(false);

  const list = useQuery({
    queryKey: ["beneficiaries", legalEntityId, employeeId],
    queryFn: () =>
      api.listBeneficiaries(legalEntityId ?? "", {
        employeeId: employeeId || undefined,
      }),
    enabled: !!legalEntityId,
  });

  const del = useMutation({
    mutationFn: (id: string) => api.deactivateBeneficiary(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["beneficiaries"] });
      qc.invalidateQueries({ queryKey: ["beneficiary-allocation"] });
    },
  });

  if (gate) return gate;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("benefits.beneficiaries_title", "Beneficiaries register")}
        subtitle={t(
          "benefits.beneficiaries_subtitle",
          "Authoritative source for EOS deceased settlement, life insurance, GOSI death pension. Allocation per payout type must sum to 100%.",
        )}
      />
      <div className="flex flex-wrap items-center gap-3">
        <Users className="h-4 w-4 text-text-secondary" />
        <input
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
          placeholder={t(
            "benefits.beneficiary_filter_emp",
            "Filter by employee UUID",
          )}
          className="w-64 rounded-lg border border-border-subtle px-3 py-1.5 font-mono text-xs"
        />
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          disabled={!employeeId}
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-brand-deep px-3 py-1.5 text-sm font-medium text-white hover:opacity-95 disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          {t("benefits.beneficiary_new", "Add a beneficiary")}
        </button>
      </div>

      {employeeId && <AllocationCard employeeId={employeeId} />}

      {list.isLoading && <Loading />}
      {list.isError && (
        <ErrorPanel message={(list.error as Error).message} />
      )}
      {list.data && list.data.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
          {t(
            "benefits.beneficiaries_empty",
            "No beneficiaries here yet — pick an employee and add the first one.",
          )}
        </div>
      )}
      {list.data && list.data.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.data.map((b) => (
            <BeneficiaryRow
              key={b.id}
              b={b}
              onDelete={() => del.mutate(b.id)}
            />
          ))}
        </div>
      )}

      {createOpen && legalEntityId && employeeId && (
        <NewBeneficiaryDialog
          legalEntityId={legalEntityId}
          defaultEmployeeId={employeeId}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ["beneficiaries"] });
            qc.invalidateQueries({ queryKey: ["beneficiary-allocation"] });
          }}
        />
      )}
    </div>
  );
}
